import React from 'react';
import { YesNoIndicators } from '../../../global/models/api-model';
import { DemographicAppKeys } from '../../../global/models/demographics-model';

export const SelfText = 'self';

export enum AEPlanType {
	None = 'None',
	Medical = 'M',
	Dental = 'D',
}

export enum AERelationshipCode {
    Child = 'CHD',
    StepChild = 'STP',
    Spouse = 'SPS',
    DomesticPartner = 'DP',
    Disabled = 'H',
}

export enum AETiers {
	Family = 'F',
	EmpPlusOne = 'T',
	Single = 'I',
	EmpPlusChild = 'G',
	EmpPlusMulti = 'D',
}

interface CodeName {
	code: string;
	name: string;
}

export interface AEPerson {
	_guid: string;
	client_nbr: string;
	disabled_child_indicator: YesNoIndicators;
	first: string;
	gender: string;
	last: string;
	middle: string;
	suffix: string;
	tax_id: string;
}

export interface AEDependent {
	person: AEPerson;
	relationship_code: AERelationshipCode;
	relationship_end_date: string;
	relationship_start_date: string;
	age_this_year: number;
	coverage_blocked: boolean;
	dep_aged_out: YesNoIndicators;
	dep_aging_out: YesNoIndicators;
}


export interface AEDefaults {
	dental_account_name: string;
	dental_account_nbr: string;
	dental_default_plan: string;
	dental_former_plan?: (CodeName)[] | null;
	dental_going_away: string;
	dental_group_id: string;
	medical_account_name: string;
	medical_account_nbr: string;
	medical_default_plan: string;
	medical_former_plan?: (CodeName)[] | null;
	medical_going_away: YesNoIndicators;
	medical_group_id: string;
}

export interface Enrollment {
	account_nbr?: string;
	code?: string;
	effective_date?: string;
	group_id?: string;
	name?: string;
	plan_info_url?: string;
	policy_nbr?: string;
	rate?: string;
	termination_date?: string;
	tier?: AETiers;
	volume?: string;
}

interface AEStatus {
	ae_available: string;
	change_request_id: number;
	effective_date: string;
	first_enrollment_allowed: string;
	group_client_number: string;
	group_id: string;
	group_name: string;
	last_admin_change_allowed: string;
	last_enrollment_allowed: string;
	last_enrollment_allowed_nominal: string;
	processing_status: string;
	submission_status_code: string;
	submission_status_desc_long: string;
	submission_status_desc_short: string;
	submitted_by: {
		client_nbr: string;
		name: string;
	};
	submitted_date: string;
}

export interface AEOfferingPlan {
	code: string;
	high_age: number;
	is_medicare_secondary_payor_eligible: YesNoIndicators;
	low_age: number;
	name: string;
    rates_by_tier: {
        D: number;
        F: number;
        G: number;
        I: number;
        T: number;
    };
    plan_info_url: string;
}

export interface AEOffering {
	available_tiers: string[];
	included: YesNoIndicators;
	plans: AEOfferingPlan[];
	tier_count?;
	tier_labels: {
		D: string;
		F: string;
		G: string;
		I: string;
		T: string;
	}
}

export interface FormInfo {
	available_workflow_actions: {
        ClearChanges: string;
        Verify: string;
		Process: string;
		Abandon: string;
		Submit: string;
		AddComment: string;
		NewForm: string;
		None: string;
	},
    change_id: string;
    change_id_signature: string;

}

export enum ProcessingStatus {
    Active = 'Active',
    Saved = 'Saved',
    Completed = 'Completed',
	Processed = 'Processed',
	Pending = 'Pending',
	Abandon = 'Abandoned',
	InProgress = 'In Progress',
	HeldForReview = 'Held for review',
	Failed = 'Failed',
}
export interface FormRecord {
    change_id: string;
    processing_category: string;
    processing_status: ProcessingStatus;
    processing_substatus: ProcessingStatus;
	submitted_by: {
        client_nbr: string;
        name: string;
	},
    submitted_date: string;
}

export interface GlobalRecord {
    date_of_qualifying_event: string;
    employee_hire_date: string;
    employee_retire_date: string;
    employee_salary: number;
	memo:string,
    special_requests: string;
	user_acknowledgement: string;
}

export interface AEInfo {
	ae_status: AEStatus;
	ae_defaults: AEDefaults;
	api_response?: { message: string, status: string };
	enrollments_base: {
		D: Enrollment[];
		M: Enrollment[];
	};
	enrollments_edited?: {
		D: Enrollment[];
		M: Enrollment[];
	};
	dep_enrollments_base: {
		[_guid: string]: {
			D: YesNoIndicators,
			M: YesNoIndicators,
		}
	},
	dep_enrollments_edited?: {
		[_guid: string]: {
			D: YesNoIndicators,
			M: YesNoIndicators,
		}
	},
	eligible_dependents: AEDependent[];
	offerings: {
		D: AEOffering;
		M: AEOffering;
	},
	processing_messages?: {
		confirm: string[],
		errors: string[],
		flags: string[],
		mc_holds: string[],
		notes: string[],
		rq_holds: string[],
		to_dos: string[],
		tx_holds: string[],
		warnings: string[],
	}

	form_info?: FormInfo,
	form_record?: FormRecord,

	global_base?: GlobalRecord,
	global_edited?: GlobalRecord,
}

export interface AETierCalculations {
	display_tier: AETiers;
	selected_tier: AETiers;
}

export enum AESteps {	
    PersonalInfo,
    Relationships,
    Coverage,
    Review,
}

export interface AnnualEnrollmentState {
	AEInfo: AEInfo;
    AEInfoLoaded: boolean;
    tierCalculations: {
        [AEPlanType.Medical]: AETierCalculations;
        [AEPlanType.Dental]: AETierCalculations;
    }
    currentStep: AESteps,
}


export enum AnnualEnrollmentKey {
	none = 'none',
	annualEnrollment = 'annualEnrollment',
	participantEnrollment = 'participantEnrollment',
	mycpgmidyear = 'mycpgmidyear',
	newHireEnrollment = 'newHireEnrollment',
	dioApproverEnrollment = 'dioApproverEnrollment',
}


export const AnnualEnrollmentContext = React.createContext(AnnualEnrollmentKey.none);

export enum AnnualEnrollmentStep {
	PersonalInfoUpdate = 'PersonalInfoUpdate',
	RelationshipUpdate = 'RelationshipUpdate',
	Coverage = 'Coverage',
	Confirmation = 'Confirmation',
}

export interface AnnualEnrollmentAppState {
	[DemographicAppKeys.myCPG]: AnnualEnrollmentState;
	[DemographicAppKeys.arc]: AnnualEnrollmentState;
}

export enum AnnualEnrollmentActionTypes {
	SET_AE_INFO = 'SET_AE_INFO',
	CLEAR_AE_INFO = 'CLEAR_AE_INFO',
	SET_AE_CALC_TIERS = 'SET_AE_CALC_TIERS',
    SET_AE_STEP = 'SET_AE_STEP',

}

export enum ChangeLabel {
    None = '',
    Added = 'Added',
    Removed = 'Removed',
}

export interface ParticipantDisplay {
    name: string;
	relationshipLabel: string;
	currentlyEnrolled?: boolean;
	changeLabel: ChangeLabel;
}